import React, { useState, useEffect } from 'react';
import { AddressRequest, AddressDto } from '../../../../services/api/contracts/address';
import { useGetAddresses, useCreateCustomerAddress } from '../../../../services/api/hooks/address';
import AddressAutocomplete, { AutocompleteAddressData } from './AddressAutocomplete';
import { SaveAddressModal } from './SaveAddressModal';
import { Button } from '../../Button';
import { ButtonVariant } from '../../buttonHelper';

interface AddressBookSelectorProps {
  mspId: string;
  customerId: string;
  /**
   * Emits the address payload (excluding name, which is managed by the parent).
   * It includes the appropriate companyId (either customerId or null) depending on "guest" mode vs "save" mode.
   */
  onChange: (payload: Omit<AddressRequest, 'name'>) => void;
  className?: string;
}

export const AddressBookSelector: React.FC<AddressBookSelectorProps> = ({
  mspId,
  customerId,
  onChange,
  className = ''
}) => {
  const [mode, setMode] = useState<'saved' | 'new'>('saved');
  const [selectedAddressId, setSelectedAddressId] = useState<string>('');
  
  const [addresses, { isLoading }] = useGetAddresses(mspId, customerId);
  const createCustomerAddress = useCreateCustomerAddress();

  const [currentNewAddress, setCurrentNewAddress] = useState<AutocompleteAddressData | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // When saved mode changes its selection, we need to bubble it up
  useEffect(() => {
    if (mode === 'saved' && selectedAddressId && addresses) {
      const addr = addresses.find((a: AddressDto) => a.id === selectedAddressId);
      if (addr) {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { id, name, ...rest } = addr;
        onChange({
          ...rest,
          companyId: customerId,
          partnerId: mspId
        });
      }
    }
  }, [mode, selectedAddressId, addresses, customerId, mspId, onChange]);

  const handleAutocompleteChange = (addressData: AutocompleteAddressData) => {
    setCurrentNewAddress(addressData);
    if (mode === 'new') {
      onChange({
        ...addressData,
        companyId: null, // Guest flow immediately
        partnerId: mspId
      });
    }
  };

  const handleSaveAddress = async (payload: AddressRequest) => {
    try {
      const newAddress = await createCustomerAddress.mutateAsync({
        ...payload,
        saveToAddressBook: true,
        mspId,
        customerId
      });
      
      // Success! Close modal, switch mode, update selected ID, and emit to parent immediately.
      setIsModalOpen(false);
      setMode('saved');
      setSelectedAddressId(newAddress.id);
      
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { id, name: _, ...rest } = newAddress;
      onChange(rest);
    } catch (e) {
      console.error('Failed to save address', e);
      throw e;
    }
  };

  return (
    <div className={`flex flex-col space-y-4 ${className}`}>
      {/* Mode Toggle */}
      <div className="flex items-center space-x-4">
        <label className="flex items-center space-x-2 text-sm text-slate-700 dark:text-slate-300">
          <input
            type="radio"
            className="text-indigo-600 focus:ring-indigo-500 h-4 w-4 border-slate-300"
            checked={mode === 'saved'}
            onChange={() => setMode('saved')}
          />
          <span>Use Saved Address</span>
        </label>
        <label className="flex items-center space-x-2 text-sm text-slate-700 dark:text-slate-300">
          <input
            type="radio"
            className="text-indigo-600 focus:ring-indigo-500 h-4 w-4 border-slate-300"
            checked={mode === 'new'}
            onChange={() => setMode('new')}
          />
          <span>Enter New Address</span>
        </label>
      </div>

      {/* Render based on Mode */}
      {mode === 'saved' ? (
        <div>
          {isLoading ? (
            <p className="text-sm text-slate-500">Loading addresses...</p>
          ) : addresses && addresses.length > 0 ? (
            <select
              className="mt-1 block w-full rounded-md border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm py-2 px-3"
              value={selectedAddressId}
              onChange={(e) => setSelectedAddressId(e.target.value)}
            >
              <option value="" disabled>Select an address...</option>
              {addresses.map((addr: AddressDto) => (
                <option key={addr.id} value={addr.id}>
                  {addr.name ? `${addr.name}: ` : ''}{addr.line1}, {addr.city}, {addr.postcode}
                </option>
              ))}
            </select>
          ) : (
            <p className="text-sm text-slate-500">No saved addresses found. Please enter a new address.</p>
          )}
        </div>
      ) : (
        <div className="flex flex-col space-y-3 p-4 border border-slate-200 dark:border-slate-800 rounded-md bg-slate-50 dark:bg-slate-800/50">
          <AddressAutocomplete 
            label="Search via Loqate"
            onChange={handleAutocompleteChange} 
          />
          
          {currentNewAddress && (
            <div className="mt-4 flex flex-col md:flex-row md:items-center justify-between gap-4 p-4 bg-white dark:bg-slate-900 rounded-md border border-slate-200 dark:border-slate-700">
              <div className="text-sm text-slate-700 dark:text-slate-300">
                <p className="font-semibold mb-1">Selected Address:</p>
                <p>{currentNewAddress.line1}</p>
                {currentNewAddress.line2 && <p>{currentNewAddress.line2}</p>}
                <p>{currentNewAddress.city}, {currentNewAddress.state}</p>
                <p>{currentNewAddress.postcode}</p>
                <p>{currentNewAddress.country}</p>
              </div>
              
              <Button 
                variant={ButtonVariant.Secondary}
                onClick={() => setIsModalOpen(true)}
                className="whitespace-nowrap"
                type="button"
              >
                Save to Address Book
              </Button>
            </div>
          )}
        </div>
      )}

      <SaveAddressModal 
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        initialData={currentNewAddress}
        onSave={handleSaveAddress}
      />
    </div>
  );
};

export default AddressBookSelector;
